import { ASSET_CLASS_IDS } from "~/consts";
import { calculatePercentChange } from "~/utils/calculatePercentChange";
import FnlSummariesNetworkV2 from "~/schemas/dbt/FnlSummariesNetworkV2";
import { MeasureValue } from "~/types";
import { mergeMeasureValues } from "~/utils/functions/mergeMeasureValues";

export interface ParentNetworkMetadata {
    id: number;
    name: string;
    slug: string;
    icon_url: string | null;
    color_hex: string | null;
}

export interface ProtocolStats {
    id: number;
    name: string;
    slug: string;
    icon_url: string | null;
    color_hex: string | null; // TODO - fix this in API layer, should be included
    asset_count: number;
    description: string | null;
    bridged_token_value_dollar: MeasureValue;
    bridged_token_market_cap_dollar: MeasureValue;
}

export interface IssuerStats {
    id: string; // QUESTION - why this mismatch? can we fix thi
    name: string;
    slug: string;
    icon_url: string | null;
    color_hex: string | null;
    asset_count: number;
    description: string | null;
    bridged_token_value_dollar: MeasureValue;
    bridged_token_market_cap_dollar: MeasureValue;
}

export interface JurisdictionCountryStats {
    id: number;
    name: string;
    slug: string;
    color_hex: string | null;
    icon_url: string | null;
    asset_count: number;
    description: string | null;
    bridged_token_value_dollar: MeasureValue;
    bridged_token_market_cap_dollar: MeasureValue;
}

export interface AssetClassStats {
    id: number;
    name: string;
    slug: string;
    icon_url: string | null;
    color_hex: string | null;
    asset_count: number;
    description: string | null;
    bridged_token_value_dollar: MeasureValue;
    bridged_token_market_cap_dollar: MeasureValue;
    holding_addresses_count: MeasureValue;
}

export interface Network extends FnlSummariesNetworkV2 {
    name: string;
    slug: string;
    website: string | null;
    asset_class_stats: AssetClassStats[];
    issuer_stats: IssuerStats[];
    jurisdiction_country_stats: JurisdictionCountryStats[];
    protocol_stats: ProtocolStats[];
    // bridged_token_market_cap_dollar: MeasureValue;
    // bridged_token_value_dollar: MeasureValue;
    // holding_addresses_count: MeasureValue;
    // trailing_30_day_transfer_volume: MeasureValue;
    // trailing_30_day_active_addresses_count: MeasureValue;
    // adjusted_daily_mints_dollar: MeasureValue;
    // adjusted_daily_mints_token: MeasureValue;
    transferability: {
        transferable: number;
        non_transferable: number;
        transferable_30d: number;
        non_transferable_30d: number;
        pct_transferable: number;
    };
}

export enum NetworkColumns {
    id = "id",
    name = "name",
    slug = "slug",
    token_count = "token_count",
    parent_network_id = "parent_network_id",
    // iconUrl = "icon_url",
    // colorHex = "color_hex",
    // assetCount = "asset_count",
    // description = "description",
    // bridgedTokenMarketCapDollar = "bridged_token_market_cap_dollar",
    // bridgedTokenValueDollar = "bridged_token_value_dollar",
    // trailing30DayTransferVolume = "trailing_30_day_transfer_volume",
    // holdingAddressesCount = "holding_addresses_count",
    // trailing30DayActiveAddressesCount = "trailing_30_day_active_addresses_count",
    // dailyMintsDollar = "adjusted_daily_mints_dollar",
    // dailyMintsToken = "adjusted_daily_mints_token",
}

// // // //
// NOTE - all the code below this line exists to normalize the data returned by the API for the NetworkTable
// This should all be either moved into the API layer, or removed once we've updated the NetworkTable to use the new data

export interface NetworkTableData extends Network {
    rank: number;
    asset_count: number | null;
    rwa_asset_count: number | null;
    stablecoin_asset_count: number | null;
    rwa_market_cap: MeasureValue;
    stablecoin_market_cap: MeasureValue;
    rwa_holders_count: MeasureValue;
    stablecoin_holders_count: MeasureValue;
    child_networks: NetworkTableData[];
    // Controls for expandable table rows
    expanded?: boolean;
    isParentNetwork?: boolean;
    onExpand?: () => void;
    rowIndex?: number; // NOTE - might not be needed
}

export enum NetworkTableDataColumns {
    id = "id",
    name = "name",
    slug = "slug",
    iconUrl = "icon_url",
    colorHex = "color_hex",
    assetCount = "asset_count",
    description = "description",
    bridgedTokenMarketCapDollar = "bridged_token_market_cap_dollar",
    bridgedTokenValueDollar = "bridged_token_value_dollar",
    trailing30DayTransferVolume = "trailing_30_day_transfer_volume",
    holdingAddressesCount = "holding_addresses_count",
    trailing30DayActiveAddressesCount = "trailing_30_day_active_addresses_count",
    dailyMintsDollar = "adjusted_daily_mints_dollar",
    dailyMintsToken = "adjusted_daily_mints_token",
    // // // //
    // TableData specific columns
    rwaAssetCount = "rwa_asset_count",
    stablecoinAssetCount = "stablecoin_asset_count",
    rwaMarketCap = "rwa_market_cap",
    stablecoinMarketCap = "stablecoin_market_cap",
    rwaHoldersCount = "rwa_holders_count",
    stablecoinHoldersCount = "stablecoin_holders_count",
    transferabilityPct = "transferability_pct",
    distributedMarketCap = "distributed_market_cap",
    isolatedMarketCap = "isolated_market_cap",
}

/**
 * Normalizes a measure value
 * @param {MeasureValue} mval - The measure value to normalize
 * @returns {MeasureValue|null} The normalized measure value, or null if the input is null
 */
function normalizeMeasureValue(mval: MeasureValue) {
    if (!mval) {
        return null;
    }

    return {
        val: mval.val,
        val_7d: mval.val_7d,
        val_30d: mval.val_30d,
        val_90d: mval.val_90d,
        chg_7d_amt: mval.chg_7d_amt,
        chg_30d_amt: mval.chg_30d_amt,
        chg_90d_amt: mval.chg_90d_amt,
        // Format percent values as decimals, for now
        chg_7d_pct: mval.chg_7d_pct ? mval.chg_7d_pct / 100 : null,
        chg_30d_pct: mval.chg_30d_pct ? mval.chg_30d_pct / 100 : null,
        chg_90d_pct: mval.chg_90d_pct ? mval.chg_90d_pct / 100 : null,
    };
}

/**
 * Subtracts two measure values
 * @param {number|null} mval1 - The first measure value
 * @param {number|null} mval2 - The second measure value
 * @returns {number|null} The result of the subtraction, or null if either value is null
 */
function subtractMeasureValues(mval1: number | null, mval2: number | null) {
    if (mval1 === null) {
        return null;
    }

    if (mval2 === null) {
        return mval1;
    }

    return mval1 - mval2;
}

/**
 * Calculates the number of holders for non-stablecoin assets on a network
 * @param {MeasureValue} holders_count - The current number of holders for non-stablecoin assets
 * @param {MeasureValue} stablecoin_holders_count - The current number of holders for stablecoin assets
 * @returns {MeasureValue|null} The number of holders for non-stablecoin assets, or null if data is unavailable
 */
function getRwaHoldersCount(
    holders_count: MeasureValue,
    stablecoin_holders_count: MeasureValue,
) {
    if (holders_count === null) {
        return null;
    }

    if (stablecoin_holders_count === null) {
        return holders_count;
    }

    // Define values for holders measure
    const val = holders_count.val - stablecoin_holders_count.val;

    // return null if val is 0 or negative
    if (val <= 0) {
        return null;
    }

    const val_7d = subtractMeasureValues(
        holders_count.val_7d,
        stablecoin_holders_count.val_7d,
    );
    const val_30d = subtractMeasureValues(
        holders_count.val_30d,
        stablecoin_holders_count.val_30d,
    );
    const val_90d = subtractMeasureValues(
        holders_count.val_90d,
        stablecoin_holders_count.val_90d,
    );

    const chg_7d_amt = subtractMeasureValues(val, val_7d);
    const chg_30d_amt = subtractMeasureValues(val, val_7d);
    const chg_90d_amt = subtractMeasureValues(val, val_7d);

    const chg_7d_pct = calculatePercentChange({
        initialValue: val_7d,
        finalValue: val,
    });
    const chg_30d_pct = calculatePercentChange({
        initialValue: val_30d,
        finalValue: val,
    });
    const chg_90d_pct = calculatePercentChange({
        initialValue: val_90d,
        finalValue: val,
    });

    return {
        val,
        val_7d,
        val_30d,
        val_90d,
        chg_7d_amt,
        chg_7d_pct,
        chg_30d_amt,
        chg_30d_pct,
        chg_90d_amt,
        chg_90d_pct,
    };
}

/**
 * Calculates the number of holders for non-stablecoin assets on a network
 * @param {MeasureValue} holders_count - The current number of holders for non-stablecoin assets
 * @param {MeasureValue} stablecoin_holders_count - The current number of holders for stablecoin assets
 * @returns {MeasureValue|null} The number of holders for non-stablecoin assets, or null if data is unavailable
 */
function getRwaTotalValue(
    rwa_value: MeasureValue,
    stablecoin_value: MeasureValue,
) {
    if (rwa_value === null) {
        return null;
    }

    if (stablecoin_value === null) {
        return rwa_value;
    }

    // Define values for holders measure
    const val = rwa_value.val - stablecoin_value.val;

    // return null if val is 0 or negative
    if (val <= 0) {
        return null;
    }

    const val_7d = subtractMeasureValues(
        rwa_value.val_7d,
        stablecoin_value.val_7d,
    );
    const val_30d = subtractMeasureValues(
        rwa_value.val_30d,
        stablecoin_value.val_30d,
    );
    const val_90d = subtractMeasureValues(
        rwa_value.val_90d,
        stablecoin_value.val_90d,
    );

    const chg_7d_amt = subtractMeasureValues(val, val_7d);
    const chg_30d_amt = subtractMeasureValues(val, val_7d);
    const chg_90d_amt = subtractMeasureValues(val, val_7d);

    const chg_7d_pct = calculatePercentChange({
        initialValue: val_7d,
        finalValue: val,
    });
    const chg_30d_pct = calculatePercentChange({
        initialValue: val_30d,
        finalValue: val,
    });
    const chg_90d_pct = calculatePercentChange({
        initialValue: val_90d,
        finalValue: val,
    });

    return {
        val,
        val_7d,
        val_30d,
        val_90d,
        chg_7d_amt,
        chg_7d_pct,
        chg_30d_amt,
        chg_30d_pct,
        chg_90d_amt,
        chg_90d_pct,
    };
}

export function getNetworkTableData(network: Network[]): NetworkTableData[] {
    const allNetworks: NetworkTableData[] = network.map((n, index) => {
        const stablecoin_stats = n.asset_class_stats.find(
            (ac) => ac.id === ASSET_CLASS_IDS.Stablecoin,
        );

        const rwa_total_value = getRwaTotalValue(
            n.bridged_token_value_dollar,
            stablecoin_stats?.bridged_token_value_dollar || null,
        );

        const asset_count = n.asset_count || 0;
        const rwa_asset_count =
            asset_count - (stablecoin_stats?.asset_count || 0);
        const stablecoin_asset_count = stablecoin_stats?.asset_count || 0;

        const holders_count = normalizeMeasureValue(
            n.holding_addresses_count || null,
        );
        const stablecoin_holders_count = normalizeMeasureValue(
            stablecoin_stats?.holding_addresses_count || null,
        );

        const rwa_holders_count = getRwaHoldersCount(
            holders_count,
            stablecoin_holders_count,
        );

        return {
            ...n,
            rank: index + 1,
            asset_count: asset_count > 0 ? asset_count : null,
            rwa_asset_count: rwa_asset_count > 0 ? rwa_asset_count : null,
            stablecoin_asset_count:
                stablecoin_asset_count > 0 ? stablecoin_asset_count : null,
            rwa_market_cap: rwa_total_value,
            stablecoin_market_cap: normalizeMeasureValue(
                stablecoin_stats?.bridged_token_market_cap_dollar || null,
            ),
            rwa_holders_count,
            stablecoin_holders_count: stablecoin_holders_count,
            child_networks: [],
        };
    });

    // Iterate through all networks and add child networks to the parent network
    allNetworks.forEach((n) => {
        const childNetworks = allNetworks.filter(
            (cn) => n.id === cn.parent_network_id,
        );

        if (childNetworks.length === 0) {
            return;
        }

        // Assign n.child_networks to the child networks
        n.child_networks = childNetworks;

        // Recalculate rwa_asset_count
        const rwa_holders_count = mergeMeasureValues(
            childNetworks.map((cn) => cn.rwa_holders_count),
        );
        const stablecoin_holders_count = mergeMeasureValues(
            childNetworks.map((cn) => cn.stablecoin_holders_count),
        );
        const rwa_market_cap = mergeMeasureValues(
            childNetworks.map((cn) => cn.rwa_market_cap),
        );
        const stablecoin_market_cap = mergeMeasureValues(
            childNetworks.map((cn) => cn.stablecoin_market_cap),
        );
        const rwa_asset_count = childNetworks.reduce((acc, cn) => {
            return acc + (cn.rwa_asset_count || 0);
        }, 0);
        const stablecoin_asset_count = childNetworks.reduce((acc, cn) => {
            return acc + (cn.stablecoin_asset_count || 0);
        }, 0);

        // Calculate transferability metrics
        const transferable: number = childNetworks.reduce((acc, cn) => {
            return acc + (cn.transferability.transferable || 0);
        }, 0);
        const non_transferable: number = childNetworks.reduce((acc, cn) => {
            return acc + (cn.transferability.non_transferable || 0);
        }, 0);
        const transferable_30d: number = childNetworks.reduce((acc, cn) => {
            return acc + (cn.transferability.transferable_30d || 0);
        }, 0);
        const non_transferable_30d: number = childNetworks.reduce((acc, cn) => {
            return acc + (cn.transferability.non_transferable_30d || 0);
        }, 0);
        const pct_transferable: number =
            transferable / (transferable + non_transferable);

        const transferability: {
            transferable: number;
            non_transferable: number;
            transferable_30d: number;
            non_transferable_30d: number;
            pct_transferable: number;
        } = {
            transferable: transferable,
            non_transferable: non_transferable,
            transferable_30d: transferable_30d,
            non_transferable_30d: non_transferable_30d,
            pct_transferable: pct_transferable,
        };

        // Assign updated values
        n.rwa_asset_count = rwa_asset_count;
        n.stablecoin_asset_count = stablecoin_asset_count;
        n.rwa_holders_count = rwa_holders_count;
        n.stablecoin_holders_count = stablecoin_holders_count;
        n.rwa_market_cap = rwa_market_cap;
        n.stablecoin_market_cap = stablecoin_market_cap;
        n.transferability = transferability;
    });

    return allNetworks;
}
